import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { LogOut, Database, Package, FileText, ClipboardList, Briefcase, Timer, FileBox, DollarSign, Palette, Receipt, CheckCircle, ShoppingCart, TrendingUp, Shield, Scissors, Factory, Box, BarChart3, Truck, Waypoints, PenTool, Pencil, BookOpen, XCircle, Store, Lightbulb, UserCog } from 'lucide-react';
import { usePermissions } from '@/hooks/use-permissions';

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions(user);
  const [accessDeniedDialog, setAccessDeniedDialog] = useState(false);
  const [deniedModule, setDeniedModule] = useState('');

  // Handle logout with redirect
  const handleLogout = () => {
    onLogout(); // Clear token and user data
    navigate('/login'); // Redirect to login page
  };

  // Handle module navigation with permission check
  const handleModuleClick = (moduleName, path, requiredPermission) => {
    if (hasPermission(requiredPermission)) {
      navigate(path);
    } else {
      setDeniedModule(moduleName);
      setAccessDeniedDialog(true);
    }
  };

  const designHubModules = [
    { title: 'Master Data Entry', description: 'Manage core business entities', icon: Database, path: '/master-data' },
    { title: 'Raw Goods Inventory', description: 'Track raw materials', icon: Package, path: '/inventory' },
    { title: 'Showroom', description: 'Displayed products information', icon: Store, path: '/finished-goods-inventory' },
    { title: 'Purchase Orders', description: 'Order raw goods for sampling', icon: FileText, path: '/purchase-orders' },
    { title: 'Design HQ', description: 'Design management', icon: Lightbulb, path: '/sample-tracker' },
    { title: 'Sampling Time Tracker', description: 'Track sampling time for articles', icon: Timer, path: '/production-unit/sampling-time-tracker' },
    { title: 'Work Orders', description: 'Internal & external work orders', icon: FileText, path: '/work-orders' },
    { title: 'Techpack', description: 'Technical specifications', icon: FileBox, path: '/techpack' },
    { title: 'Costing', description: 'Calculate product costs', icon: DollarSign, path: '/costing' },
    { title: 'External Work Orders', description: 'Manage external work orders for sampling', icon: FileText, path: '/external-work-orders' },
    { title: 'Expenses', description: 'Manage company expenses', icon: Receipt, path: '/expenses' },
  ];

  const productionUnitModules = [
    { title: 'Production Approved Article Data', description: 'Articles ready for production', icon: CheckCircle, path: '/production-articles' },
    { title: 'Production Orders', description: 'Manage bulk production orders', icon: ShoppingCart, path: '/production-orders' },
    { title: 'Purchase Orders', description: 'Production purchase orders', icon: FileText, path: '/production-purchase-orders' },
    { title: 'Raw Goods Inventory', description: 'Production raw materials', icon: Package, path: '/production-raw-inventory' },
    { title: 'Finished Goods Inventory', description: 'Completed production items', icon: Box, path: '/production-finished-inventory' },
    { title: 'Production Planning & Tracking', description: 'Plan and track production', icon: TrendingUp, path: '/production-tracking' },
    { title: 'Quality Control', description: 'Inspections and quality checks', icon: Shield, path: '/quality-control' },
    { title: 'Cutting Room', description: 'Fabric cutting operations', icon: Scissors, path: '/cutting-room' },
    { title: 'Sewing Floor', description: 'Sewing operations management', icon: Factory, path: '/sewing-floor' },
    { title: 'Finish, Folding, Packing', description: 'Final finishing and packing', icon: Box, path: '/finishing-packing' },
    { title: 'Production Reports', description: 'Analytics and metrics', icon: BarChart3, path: '/production-reports' },
    { title: 'Delivery Management', description: 'Outbound logistics', icon: Truck, path: '/delivery-management' },
    { title: 'Production Costing', description: 'Actual costs vs estimates', icon: DollarSign, path: '/production-costing' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-black" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>MNTX CITY OFFICE</h1>
            <p className="text-sm text-gray-600 mt-1">Welcome back, <span className="font-semibold">{user?.name || 'User'}</span></p>
          </div>
          <Button onClick={handleLogout} variant="outline" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {/* Design Studio Card */}
          <Card
            className="hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-black"
            onClick={() => handleModuleClick('Design Studio', '/design-studio', 'design_studio_access')}
            data-testid="design-hub-card"
          >
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-lg bg-black flex items-center justify-center">
                  <Palette className="w-8 h-8 text-white" />
                </div>
              </div>
              <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Design Studio
              </CardTitle>
              <CardDescription className="text-sm mt-2">
                Sampling, design management, and creative operations
              </CardDescription>
            </CardHeader>
          </Card>

          {/* Task Management Card */}
          <Card
            className="hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-black"
            onClick={() => handleModuleClick('Task Management', '/task-management', 'task_management_access')}
            data-testid="task-management-card"
          >
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-lg bg-black flex items-center justify-center">
                  <Waypoints className="w-8 h-8 text-white" />
                </div>
              </div>
              <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Task Management
              </CardTitle>
              <CardDescription className="text-sm mt-2">
                Task creation, assignment, tracking, and reporting
              </CardDescription>
            </CardHeader>
          </Card>

          {/* User Access Control Card */}
          <Card
            className="hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-black"
            onClick={() => handleModuleClick('User Access Control', '/user-access-control', 'user_access_control')}
            data-testid="user-access-control-card"
          >
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-lg bg-black flex items-center justify-center">
                  <UserCog className="w-8 h-8 text-white" />
                </div>
              </div>
              <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                User Access Control
              </CardTitle>
              <CardDescription className="text-sm mt-2">
                Manage user permissions and access rights
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </main>

      {/* Access Denied Dialog */}
      <Dialog open={accessDeniedDialog} onOpenChange={setAccessDeniedDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl">Access Denied</DialogTitle>
            <DialogDescription className="text-center text-base mt-4">
              You do not have permission to access the <span className="font-semibold">{deniedModule}</span> module.
              <br />
              <br />
              Please contact your administrator to request access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="justify-center mt-4">
            <Button 
              onClick={() => setAccessDeniedDialog(false)}
              className="bg-black hover:bg-gray-800"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
